/**
 * Typesense filter field isimleri ve ID/name eşleme yardımcıları.
 *
 * Filtreleme her zaman ID field'ları üzerinden yapılır.
 * Name field'ları yalnızca badge / display için tutulur.
 */

export const BRAND_ID_FIELD = "brand.id";
export const BRAND_NAME_FIELD = "brand.name";
export const CATEGORY_ID_FIELD = "categories.categoryId";
export const CATEGORY_NAME_FIELD = "categories.name";
export const MERCHANT_NAME_FIELD = "merchant.name";
export const SELLER_ID_FIELD = "seller.id";
export const SELLER_NAME_FIELD = "seller.name";

/** Sidebar / modal'da persist edilen facet key'leri (filtreleme kaynağı) */
export const PERSISTED_FACET_FIELDS = [
  BRAND_ID_FIELD,
  CATEGORY_ID_FIELD,
  MERCHANT_NAME_FIELD,
] as const;

/** Facet sıralama önceliği: Marka → Kategori → Satıcı */
export const PERSISTED_FACET_PRIORITY = [
  BRAND_ID_FIELD,
  CATEGORY_ID_FIELD,
  MERCHANT_NAME_FIELD,
] as const;

/** filter_by'a asla yazılmayan display-only key'ler */
export const DISPLAY_ONLY_FILTER_FIELDS = new Set<string>([
  BRAND_NAME_FIELD,
  CATEGORY_NAME_FIELD,
  SELLER_NAME_FIELD,
]);

export function isDisplayOnlyFilterField(field: string): boolean {
  return (
    DISPLAY_ONLY_FILTER_FIELDS.has(field) ||
    field.startsWith("attributes.attributeValueLabel::") ||
    field.startsWith("attributes.attributeName::")
  );
}

export function isEffectivePersistedFilterField(
  field: string,
  filters: Record<string, string[] | undefined>,
): boolean {
  if (isDisplayOnlyFilterField(field)) return false;
  const groupPrefix = "attributes.attributeValueId::";
  if (!field.startsWith(groupPrefix)) return true;
  const suffix = field.slice(groupPrefix.length);
  return (filters[`attributes.attributeName::${suffix}`] || []).length > 0;
}

export function isPersistedFacetField(field: string): boolean {
  return (PERSISTED_FACET_FIELDS as readonly string[]).includes(field);
}

export function getDisplayFieldForIdField(idField: string): string | null {
  if (idField === BRAND_ID_FIELD) return BRAND_NAME_FIELD;
  if (idField === CATEGORY_ID_FIELD) return CATEGORY_NAME_FIELD;
  if (idField === SELLER_ID_FIELD) return SELLER_NAME_FIELD;
  return null;
}

/**
 * ID listesine değer ekle/çıkar; paralel name listesini aynı index'ten senkronize et.
 */
export function toggleParallelIdName(
  filters: Record<string, string[]>,
  idField: string,
  valueId: string,
  displayName?: string,
): Record<string, string[]> {
  const nameField = getDisplayFieldForIdField(idField);
  const currentIds = filters[idField] || [];
  const isRemoving = currentIds.includes(valueId);
  const nextIds = isRemoving
    ? currentIds.filter((value) => value !== valueId)
    : [...currentIds, valueId];

  const next: Record<string, string[]> = { ...filters, [idField]: nextIds };

  if (!nameField) return next;

  const currentNames = filters[nameField] || [];
  if (isRemoving) {
    const index = currentIds.indexOf(valueId);
    next[nameField] =
      index >= 0
        ? currentNames.filter((_, i) => i !== index)
        : currentNames.filter((name) => name !== displayName);
  } else if (displayName) {
    next[nameField] = [...currentNames, displayName];
  }

  return next;
}

export function escapeTypesenseValue(value: string): string {
  const escaped = value.replace(/`/g, "\\`");
  if (
    escaped.includes(" ") ||
    escaped.includes(",") ||
    escaped.includes(":") ||
    escaped.includes("`")
  ) {
    return `\`${escaped}\``;
  }
  return escaped;
}
